import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  BunnyVideoStatusService,
  VideoProcessingStatus as VideoStatus,
} from '../../services/bunny-video-status.service';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HttpClientModule } from '@angular/common/http';
import { IconComponent } from '../icon/icon.component';
import { WatchProgressChipComponent } from '../watch-progress-chip/watch-progress-chip.component';
import { PlayUrls } from '../../types/video.types';
import Hls from 'hls.js';
import { BunnyPlayerControlsService } from './services/bunny-player-controls.service';
import { BunnyPlayerSpeedService } from './services/bunny-player-speed.service';
import { environment } from '../../../../environments/environment';
import {
  formatTime,
  chooseHlsPlaybackPath,
  classifyHlsError,
  isEmptySrcPlaceholderError,
  MAX_HLS_NETWORK_RETRIES,
} from './pure-functions/bunny-player-utils';
import { DebugService } from '../../services/debug.service';
import { isIphoneClassDevice } from '../../utils/fullscreen.utils';
import {
  VideoQualityLevel,
  VideoQualitySelectorComponent,
} from './video-quality-selector/video-quality-selector.component';

const VIDEO_QUALITY_PREFERENCE_KEY = 'video-quality-preference';

@Component({
  selector: 'app-bunny-player',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    IconComponent,
    WatchProgressChipComponent,
    VideoQualitySelectorComponent,
  ],
  templateUrl: './bunny-player.component.html',
  styles: [
    `
      /* Only essential video-specific styles that DaisyUI doesn't provide */
      video {
        pointer-events: auto;
      }

      iframe {
        pointer-events: auto;
      }
    `,
  ],
  providers: [BunnyPlayerControlsService, BunnyPlayerSpeedService],
})
export class BunnyPlayerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() videoUrl: string = '';
  @Input() videoId: string = ''; // Bunny video ID for secure access
  @Input() playUrls: PlayUrls | undefined; // Play URLs from API (preferred)
  @Input() defaultPlayUrl: string = ''; // Default play URL from API (preferred)
  // Per-org Bunny: the video's OWN CDN host (videos.cdn_hostname, e.g.
  // vz-9cdfadfc-a5d.b-cdn.net). Used to build the HLS playlist URL when the
  // player can't extract a CDN host from the URL (iframe-embed URLs / bare
  // videoId) and no backend playUrls are available. Falls back to the shared
  // host only when this is empty (legacy / NULL-library videos).
  @Input() cdnHostname: string = '';
  @Input() isThumbnailView = false;
  @Input() startPosition: number = 0;
  @Input() maxWatchedPosition: number = 0; // For progress tracking
  @Input() restrictForwardSeek: boolean = false; // Prevent seeking ahead of watched content
  @Input() debugVideoName: string = '';
  /**
   * KAN-306: opt in to the in-player watch-progress chip. Off by default
   * because the chip is only honest where a parent actually feeds the
   * max-watched metric. The instructor module preview (view-module-modal)
   * embeds this player without binding [maxWatchedPosition], so its percentage
   * is a permanent 0 -- it used to render "(Progress: 0%)" over every preview,
   * and an un-gated chip would upgrade that to a "Not started" pill sitting
   * beside a playing video.
   */
  @Input() showWatchProgress = false;
  /**
   * The parent's videoCompleted / videoCompletedFromBackend, for the chip's
   * terminal state. Unlike the percentage this needs no synchronous push: it
   * is never recomputed inside this component, so the ordinary binding lands in
   * the same change-detection pass that repaints the parent's own chip.
   */
  @Input() watchCompleted = false;
  /**
   * The parent's authoritative watch percentage -- the DECLARATIVE half of
   * syncWatchedPercentage() (KAN-306 post-QA).
   *
   * The imperative push only fires on playback ticks, and this component's own
   * updateProgressDisplay() cannot compute anything until the video reports a
   * duration. That left a mount window where the percentage was still its 0
   * initializer while [watchCompleted] -- which needs neither -- had already
   * hydrated from the parent's moduleProgress, so the chip rendered a check
   * icon against "0%". Binding the number as an @Input closes the window from
   * both sides: it lands on the child's very first ngOnChanges, whichever of
   * the progress GET / video metadata / player mount wins the race.
   *
   * Parents that DON'T bind this (view-module-modal's instructor preview) are
   * unaffected: no change ever fires, so updateProgressDisplay() keeps sole
   * ownership of the field exactly as before.
   */
  @Input() watchedPercentage = 0;
  @Output() videoEnded = new EventEmitter<void>();
  @Output() videoClick = new EventEmitter<void>();
  @Output() videoReady = new EventEmitter<HTMLVideoElement | HTMLIFrameElement>();
  @Output() timeUpdate = new EventEmitter<{currentTime: number, duration: number}>();
  @Output() userEngaged = new EventEmitter<void>(); // Emit when user explicitly starts video
  @Output() videoLoaded = new EventEmitter<{duration: number}>(); // PHASE 2: Emit when video metadata loads

  @ViewChild('videoPlayer', { static: false }) videoPlayer?: ElementRef<HTMLVideoElement>;
  @ViewChild('iframePlayer', { static: false }) iframePlayer?: ElementRef<HTMLIFrameElement>;
  @ViewChild('progressBar', { static: false }) progressBar?: ElementRef<HTMLDivElement>;

  safeEmbedUrl: SafeResourceUrl | null = null;
  isBunnyUrl = false;
  isProcessing = false;
  videoStatus: VideoStatus | null = null;

  private statusCheckSubscription: Subscription | null = null;
  private trackedVideoId: string | null = null;
  private hls: Hls | null = null;
  private pendingCanplayHandler: (() => void) | null = null;
  private cdr = inject(ChangeDetectorRef);

  // KAN-306 round 4 (A3 hard-refresh bug): true once a real resume position
  // has been applied for the CURRENT video -- either by onVideoLoaded()
  // finding a nonzero startPosition already available at 'loadedmetadata'
  // time, or by the one-time catch-up below when startPosition arrives
  // LATE (a real race on a hard page reload, where the progress GET can
  // resolve after 'loadedmetadata' already fired with startPosition still
  // 0). Reset whenever the video source truly changes. Deliberately does
  // NOT retrigger on every subsequent startPosition tick during live
  // playback -- that would reintroduce the same accrual-inflation/
  // resume-clobber bug this round fixed for maxWatchedPosition.
  private hasAppliedResumeSeek = false;

  /**
   * KAN-576: true once playback has failed unrecoverably (most often a
   * playlist.m3u8 404 from a video requested on the wrong CDN host). Without
   * it the player retried the manifest forever and showed a blank grey box at
   * 0:00 / 0:00 with no message -- a silent failure the viewer cannot act on.
   */
  playbackFailed = false;

  /** Fatal fragment-network retries spent on the CURRENT source (KAN-576). */
  private hlsNetworkRetries = 0;

  // Processed video URL (from videoId or videoUrl)
  processedVideoUrl: string = '';
  isLoadingVideo: boolean = false;
  isHlsStream: boolean = false;
  qualityLevels: VideoQualityLevel[] = [];
  selectedQualityLevel = -1;
  activeQualityLevel = -1;
  manualQualitySelectionAvailable = false;

  // Expose pure function for template
  formatTime = formatTime;

  constructor(
    private sanitizer: DomSanitizer,
    private videoStatusService: BunnyVideoStatusService,
    public controlsService: BunnyPlayerControlsService,
    public speedService: BunnyPlayerSpeedService,
    public debug: DebugService
  ) {}

  /**
   * Get current playback position from video element (canonical source)
   */
  get currentPosition(): number {
    return this.videoPlayer?.nativeElement?.currentTime || 0;
  }

  onVideoEnded() {
    this.controlsService.onVideoEndedSnap();
    this.videoEnded.emit();
  }

  onVideoClick() {
    if (this.isThumbnailView) {
      this.videoClick.emit();
    }
  }

  ngOnInit() {
    this.processVideoUrl();
    this.speedService.loadUserSpeedPreference();

    // Always show play overlay initially unless in thumbnail view
    this.controlsService.showPlayOverlay = !this.isThumbnailView;
    this.controlsService.hasUserStartedVideo = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['videoUrl'] || changes['videoId'] || changes['playUrls'] || changes['defaultPlayUrl'] || changes['cdnHostname']) {
      const isFirstChange = (changes['videoUrl']?.firstChange ?? true) &&
                            (changes['videoId']?.firstChange ?? true) &&
                            (changes['playUrls']?.firstChange ?? true) &&
                            (changes['defaultPlayUrl']?.firstChange ?? true) &&
                            (changes['cdnHostname']?.firstChange ?? true);

      this.processVideoUrl();

      // When the video source changes after initial load, reinitialize the player
      if (!isFirstChange) {
        this.reinitializePlayer();
      }
    }

    // KAN-306 round 4: maxWatchedPosition changes ONLY refresh the progress
    // DISPLAY. This used to also force-seek the video to
    // this.maxWatchedPosition on every change -- but maxWatchedPosition is
    // cumulativeWatchTime, a coverage TIME-SUM (round 2), not a playhead
    // POSITION, and it now changes on every single progress tick (round 1's
    // live-display fix). For an HLS stream that meant registering a fresh
    // 'canplay' listener on every tick; if 'canplay' ever refires during
    // live playback (a real hls.js behavior during ABR quality switches /
    // buffer recoveries), the stale handler force-seeked the video to
    // whatever the sum was at that moment -- injecting an unnatural
    // currentTime jump that VideoProgressService's delta-gate then
    // miscredited as genuine forward playback (the measured ~15% accrual
    // inflation), and could also clobber a correct earlier resume-seek
    // (from onVideoLoaded()'s startPosition-based logic, round 2) with the
    // wrong, sum-based position. Resume-seeking is the SOLE responsibility
    // of onVideoLoaded() (startPosition, never maxWatchedPosition).
    if (changes['maxWatchedPosition']) {
      this.controlsService.updateProgressDisplay(this.maxWatchedPosition);
    }

    // AFTER updateProgressDisplay() on purpose: when a parent feeds the metric,
    // its number is the authoritative one (that is the whole point of KAN-306 --
    // one number, one source), so it must win the pass rather than be
    // overwritten by this component's independent duration-derived recompute.
    if (changes['watchedPercentage']) {
      this.syncWatchedPercentage(this.watchedPercentage);
    }

    // KAN-306 round 4 (A3): one-time catch-up for a LATE-arriving resume
    // position. On a hard page reload, the progress GET can resolve AFTER
    // 'loadedmetadata' already fired with startPosition still 0, so
    // onVideoLoaded() seeked to 0 with nothing to resume from. When the
    // real position arrives, apply it once here -- keyed on startPosition
    // (the true resume POINT), never maxWatchedPosition (the coverage sum).
    // hasAppliedResumeSeek prevents this from firing again on every
    // subsequent startPosition tick during live playback.
    if (changes['startPosition'] && !this.hasAppliedResumeSeek && this.startPosition > 0) {
      this.seekToResumePosition(this.startPosition);
    }
  }

  /**
   * Seek to the given resume POSITION (never a coverage sum). Shared by
   * onVideoLoaded() and the late-arrival catch-up in ngOnChanges.
   */
  private seekToResumePosition(position: number): void {
    if (!this.videoPlayer?.nativeElement) return;
    const video = this.videoPlayer.nativeElement;

    if (this.isHlsStream || video.readyState < 3) {
      if (this.pendingCanplayHandler) {
        video.removeEventListener('canplay', this.pendingCanplayHandler);
        this.pendingCanplayHandler = null;
      }
      const canplayHandler = () => {
        video.currentTime = position;
        this.controlsService.currentTime = position;
        this.pendingCanplayHandler = null;
        this.cdr.markForCheck();
      };
      this.pendingCanplayHandler = canplayHandler;
      video.addEventListener('canplay', canplayHandler, { once: true });
    } else {
      video.currentTime = position;
      this.controlsService.currentTime = position;
    }
    this.hasAppliedResumeSeek = true;
  }

  ngAfterViewInit() {
    // Register DOM elements with services
    setTimeout(() => {
      console.log('[BUNNY_PLAYER] ngAfterViewInit check:', {
        hasVideoPlayer: !!this.videoPlayer?.nativeElement,
        hasIframePlayer: !!this.iframePlayer?.nativeElement,
        videoId: this.videoId,
        videoUrl: this.videoUrl,
        isHlsStream: this.isHlsStream,
        processedVideoUrl: this.processedVideoUrl
      });

      // Register elements with controls service
      this.controlsService.registerElements(
        this.videoPlayer?.nativeElement || null,
        null,
        this.progressBar?.nativeElement || null
      );

      // Register callbacks for @Output emitters
      this.controlsService.registerCallbacks({
        emitTimeUpdate: (data) => this.timeUpdate.emit(data),
        emitVideoLoaded: (data) => this.videoLoaded.emit(data),
        emitUserEngaged: () => this.userEngaged.emit(),
        emitVideoEnded: () => this.videoEnded.emit(),
        getShowSpeedMenu: () => this.speedService.showSpeedMenu,
        markForCheck: () => this.cdr.markForCheck()
      });

      // Register elements with speed service
      this.speedService.registerElements(
        this.videoPlayer?.nativeElement || null,
        this.iframePlayer?.nativeElement || null,
        this.isBunnyUrl,
        () => this.controlsService.onSpeedMenuToggled(this.speedService.showSpeedMenu)
      );

      if (this.videoPlayer?.nativeElement) {
        console.log('[BUNNY_PLAYER] Emitting videoReady with HTMLVideoElement');
        this.videoReady.emit(this.videoPlayer.nativeElement);

        // Initialize HLS if this is an HLS stream
        if (this.isHlsStream && this.processedVideoUrl.endsWith('.m3u8')) {
          console.log('[BUNNY_PLAYER] Initializing HLS from ngAfterViewInit');
          this.initializeHls();
        }

        // Set start position if provided (for non-HLS videos)
        if (this.startPosition > 0 && !this.isHlsStream) {
          this.videoPlayer.nativeElement.currentTime = this.startPosition;
        }
      } else if (this.iframePlayer?.nativeElement) {
        console.log('[BUNNY_PLAYER] Emitting videoReady with HTMLIFrameElement');
        this.videoReady.emit(this.iframePlayer.nativeElement);
        // Note: iframe mode is currently disabled in favor of HTML5 video
      } else {
        console.log('[BUNNY_PLAYER] No video element available to emit');
      }

      this.cdr.markForCheck();
    }, 100);
  }

  ngOnDestroy() {
    if (this.statusCheckSubscription) {
      this.statusCheckSubscription.unsubscribe();
    }
    // Clean up services
    this.controlsService.destroy();
    this.speedService.destroy();
    // Clean up HLS instance
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }

  // Thin wrappers that delegate to controls service and pass @Input values

  onTimeUpdate(event: Event) {
    this.controlsService.onTimeUpdate(event, this.maxWatchedPosition);
  }

  /**
   * KAN-306 round 5: called by the parent (split-screen-player) SYNCHRONOUSLY
   * right after it computes this tick's authoritative watch percentage, so
   * this component's watch-progress chip always renders the exact same integer
   * as the parent's own chip in the status bar. Without this, the number
   * -- computed inside onTimeUpdate() from the current (possibly one-tick
   * stale) `maxWatchedPosition` @Input -- could render 1 percentage point
   * behind the status bar, which recomputes from the freshly-updated value
   * within the same event before the @Input binding has propagated down.
   * This is a zoneless app, so a change made outside a template event
   * handler needs an explicit markForCheck() to render.
   */
  syncWatchedPercentage(percentage: number): void {
    if (this.controlsService.watchedPercentage === percentage) {
      return;
    }
    this.controlsService.watchedPercentage = percentage;
    this.cdr.markForCheck();
  }

  onVideoLoaded() {
    this.controlsService.onVideoLoaded(
      this.maxWatchedPosition,
      this.startPosition,
      this.isHlsStream,
      this.isBunnyUrl,
      this.isThumbnailView,
      this.speedService.playbackSpeed
    );
    // KAN-306 round 4: if a real resume position was already available at
    // 'loadedmetadata' time, this WAS the resume seek -- no further
    // catch-up should fire later in ngOnChanges. If startPosition is still
    // 0 here (the GET hasn't resolved yet), leave the flag false so the
    // one-time catch-up can still apply once the real value arrives.
    if (this.startPosition > 0) {
      this.hasAppliedResumeSeek = true;
    }
  }

  onSeeking(event: Event) {
    this.controlsService.onSeeking(event, this.restrictForwardSeek, this.maxWatchedPosition);
  }

  onSeeked(event: Event) {
    this.controlsService.onSeeked(event, this.restrictForwardSeek, this.maxWatchedPosition);
  }

  seekToPosition(event: MouseEvent) {
    this.cancelPendingCanplaySeek();
    this.controlsService.seekToPosition(event, this.restrictForwardSeek, this.maxWatchedPosition);
  }

  private cancelPendingCanplaySeek(): void {
    if (this.pendingCanplayHandler && this.videoPlayer?.nativeElement) {
      this.videoPlayer.nativeElement.removeEventListener('canplay', this.pendingCanplayHandler);
      this.pendingCanplayHandler = null;
    }
  }

  /**
   * Initialize HLS.js for adaptive streaming
   * Safari supports HLS natively, other browsers need hls.js
   */
  private initializeHls(): void {
    if (!this.videoPlayer?.nativeElement) return;

    const video = this.videoPlayer.nativeElement;

    const hlsSupported = Hls.isSupported();
    const nativeHlsSupport = video.canPlayType('application/vnd.apple.mpegurl');
    // KAN-635: hls.js everywhere except iPhone, which plays natively so its
    // native fullscreen player works. See chooseHlsPlaybackPath.
    const playbackPath = chooseHlsPlaybackPath(hlsSupported, nativeHlsSupport, isIphoneClassDevice());
    console.log('[BUNNY_PLAYER] Initializing HLS:', {
      url: this.processedVideoUrl,
      hlsSupported,
      nativeHlsSupport,
      playbackPath
    });

    if (playbackPath === 'hlsjs') {
      console.log('[BUNNY_PLAYER] Using hls.js for HLS support');

      // Clean up existing HLS instance
      if (this.hls) {
        this.hls.destroy();
      }

      // Create new HLS instance
      this.hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      });

      // Attach media element
      this.hls.attachMedia(video);

      // Load the HLS stream
      this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.log('[BUNNY_PLAYER] HLS media attached, loading source');
        this.hls!.loadSource(this.processedVideoUrl);
      });

      // Handle manifest parsed event
      this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        console.log('[BUNNY_PLAYER] HLS manifest parsed:', {
          levels: data.levels.length,
          qualities: data.levels.map(l => `${l.width}x${l.height}`)
        });
        this.onHlsManifestParsed(data.levels);
      });

      this.hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
        this.activeQualityLevel = data.level;
        this.cdr.markForCheck();
      });

      // Handle errors
      this.hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('[BUNNY_PLAYER] HLS error:', data);

        // KAN-576: a manifest that will not load is TERMINAL. This used to
        // answer every fatal network error with startLoad(), so a 404 on
        // playlist.m3u8 (a per-org video requested on the shared CDN host)
        // retried forever behind a blank player and never told the viewer
        // anything.
        switch (classifyHlsError(
          data.fatal,
          String(data.type),
          String(data.details),
          this.hlsNetworkRetries,
          MAX_HLS_NETWORK_RETRIES
        )) {
          case 'retry':
            this.hlsNetworkRetries++;
            console.log(
              `[BUNNY_PLAYER] Fatal fragment network error, retry ${this.hlsNetworkRetries}/${MAX_HLS_NETWORK_RETRIES}`
            );
            this.hls!.startLoad();
            break;
          case 'recover':
            console.log('[BUNNY_PLAYER] Fatal media error, trying to recover');
            this.hls!.recoverMediaError();
            break;
          case 'fail':
            console.error('[BUNNY_PLAYER] Unrecoverable playback error, surfacing to the viewer');
            this.hls!.destroy();
            this.onPlaybackFailed();
            break;
          // 'ignore': non-fatal, hls.js handles it internally.
        }
      });
    } else if (playbackPath === 'native') {
      console.log('[BUNNY_PLAYER] Using native HLS support');
      this.resetQualityState();
      video.src = this.processedVideoUrl;
    } else {
      this.resetQualityState();
      console.error('[BUNNY_PLAYER] HLS is not supported in this browser');
    }
  }

  /**
   * KAN-576: render the failure instead of a blank player. Fires from an
   * hls.js callback, which is outside Angular's change detection in a zoneless
   * app, so the CD kick is required for the template to update at all.
   */
  /**
   * KAN-576 follow-up: the <video> element's own error event. hls.js is only
   * one playback path -- native HLS (Safari/iOS) and plain mp4 assign
   * video.src directly and never reach the hls.js ERROR handler, so a 404 on
   * those paths stayed silent even after the hls.js fix.
   */
  onVideoElementError(): void {
    // An empty src is not a failure: the element reports an error when the
    // binding is cleared between sources.
    if (this.processedVideoUrl === '') return;
    // KAN-576 hotfix: nor is the browser rejecting the empty placeholder src it
    // sees before hls.js attaches -- that error arrives AFTER processedVideoUrl
    // is set, so the guard above cannot catch it. See isEmptySrcPlaceholderError.
    const video = this.videoPlayer?.nativeElement;
    if (video && isEmptySrcPlaceholderError(video.error?.code, video.currentSrc)) {
      // Leave a trail: a swallowed error that turns out to be real is otherwise invisible.
      console.debug('[BUNNY_PLAYER] Ignoring empty-src placeholder error (hls.js attach pending)', {
        code: video.error?.code,
        message: video.error?.message,
        currentSrc: video.currentSrc,
        url: this.processedVideoUrl,
      });
      return;
    }
    console.error('[BUNNY_PLAYER] <video> element error for', this.processedVideoUrl, video?.error);
    this.onPlaybackFailed();
  }

  onPlaybackFailed(): void {
    this.playbackFailed = true;
    this.isLoadingVideo = false;
    this.cdr.detectChanges();
  }

  onHlsManifestParsed(levels: Array<{ height: number }>): void {
    this.qualityLevels = levels
      .map((level, index) => ({ index, height: level.height }))
      .filter((level) => Number.isFinite(level.height) && level.height > 0)
      .sort((left, right) => right.height - left.height);
    this.manualQualitySelectionAvailable = this.qualityLevels.length > 0;
    this.restoreQualityPreference();
    this.cdr.markForCheck();
  }

  onQualitySelected(levelIndex: number): void {
    if (!this.hls) return;

    if (levelIndex === -1) {
      this.hls.nextLevel = -1;
      this.selectedQualityLevel = -1;
      sessionStorage.setItem(VIDEO_QUALITY_PREFERENCE_KEY, 'auto');
      this.cdr.markForCheck();
      return;
    }

    const selected = this.qualityLevels.find((level) => level.index === levelIndex);
    if (!selected) return;

    // nextLevel changes rendition at the next safe fragment boundary. Unlike
    // currentLevel, it does not flush the buffer and interrupt playback.
    this.hls.nextLevel = selected.index;
    this.selectedQualityLevel = selected.index;
    sessionStorage.setItem(VIDEO_QUALITY_PREFERENCE_KEY, String(selected.height));
    this.cdr.markForCheck();
  }

  private restoreQualityPreference(): void {
    if (!this.hls) return;

    const preference = sessionStorage.getItem(VIDEO_QUALITY_PREFERENCE_KEY);
    if (!preference || preference === 'auto') {
      this.hls.nextLevel = -1;
      this.selectedQualityLevel = -1;
      return;
    }

    const preferredHeight = Number(preference);
    const preferredLevel = this.qualityLevels.find((level) => level.height === preferredHeight);
    if (!preferredLevel) {
      this.hls.nextLevel = -1;
      this.selectedQualityLevel = -1;
      return;
    }

    this.hls.nextLevel = preferredLevel.index;
    this.selectedQualityLevel = preferredLevel.index;
  }

  private resetQualityState(): void {
    this.qualityLevels = [];
    this.selectedQualityLevel = -1;
    this.activeQualityLevel = -1;
    this.manualQualitySelectionAvailable = false;
  }

  /**
   * Reinitialize the player when the video source changes.
   * Destroys the existing HLS instance, resets playback state,
   * and sets up the new video source.
   */
  private reinitializePlayer(): void {
    // Destroy existing HLS instance
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    this.resetQualityState();

    // Cancel any pending canplay seek from the previous video
    this.cancelPendingCanplaySeek();

    // KAN-306 round 4: a new video source means a fresh resume-seek cycle.
    this.hasAppliedResumeSeek = false;

    // Reset controls state for the new video
    this.controlsService.showPlayOverlay = !this.isThumbnailView;
    this.controlsService.hasUserStartedVideo = false;
    this.controlsService.currentTime = 0;
    this.controlsService.duration = 0;
    this.controlsService.currentPercentage = 0;
    // NOT a hardcoded 0: a source swap re-mounts the video but not this
    // component, so the parent's number is still valid and no @Input change
    // will fire to restore it if the new module's percentage happens to match
    // the old one. Falls back to 0 for parents that feed nothing.
    this.controlsService.watchedPercentage = this.watchedPercentage;

    // Reinitialize HLS or set video src after a tick to let Angular update the template
    if (this.videoPlayer?.nativeElement && this.processedVideoUrl) {
      const video = this.videoPlayer.nativeElement;

      if (this.isHlsStream && this.processedVideoUrl.endsWith('.m3u8')) {
        this.initializeHls();
      } else {
        // For non-HLS, update the src directly (Angular binding handles this
        // via [src] but we also need to load the new source)
        video.load();
      }
    }
  }

  private processVideoUrl() {
    // Reset state
    this.playbackFailed = false;
    this.hlsNetworkRetries = 0;
    this.processedVideoUrl = '';
    this.isLoadingVideo = false;
    this.isBunnyUrl = false;
    this.isHlsStream = false;

    // Priority 1: Use defaultPlayUrl from API if provided (most reliable)
    if (this.defaultPlayUrl && this.defaultPlayUrl !== '') {
      console.log('[BUNNY_PLAYER] Using API-provided defaultPlayUrl:', this.defaultPlayUrl);
      this.processedVideoUrl = this.defaultPlayUrl;
      this.isHlsStream = this.defaultPlayUrl.endsWith('.m3u8');
      return;
    }

    // Priority 2: Use playUrls from API if provided - PREFER HLS for adaptive streaming
    if (this.playUrls) {
      // Prefer HLS for adaptive streaming, fallback to MP4
      const preferredUrl = this.playUrls.hls || this.playUrls.mp4720 || this.playUrls.mp41080 ||
                          this.playUrls.mp4480 || this.playUrls.mp4360 ||
                          this.playUrls.mp4240;
      if (preferredUrl) {
        console.log('[BUNNY_PLAYER] Using API-provided playUrls:', preferredUrl);
        this.processedVideoUrl = preferredUrl;
        this.isHlsStream = preferredUrl.endsWith('.m3u8');
        return;
      }
    }

    // Priority 3: Use videoUrl if provided and is a full URL
    if (this.videoUrl && (this.videoUrl.startsWith('http://') || this.videoUrl.startsWith('https://'))) {
      this.processedVideoUrl = this.videoUrl;
      this.processExistingVideoUrl();
      return;
    }

    // Priority 4: Convert videoId to playable URL (legacy fallback)
    if (this.videoId && this.videoId !== '') {
      this.convertVideoIdToUrl();
      return;
    }

    // Priority 5: Try to extract video ID from videoUrl if it's not a full URL
    if (this.videoUrl && this.videoUrl !== '') {
      const videoIdMatch = this.videoUrl.match(/([a-f0-9\-]{36})/i);
      if (videoIdMatch && videoIdMatch[1]) {
        this.videoId = videoIdMatch[1];
        this.convertVideoIdToUrl();
        return;
      }
    }

  }

  private processExistingVideoUrl() {
    // Check if this is a Bunny Stream embed URL - convert it to HLS for HTML5 player
    const isBunnyEmbedUrl =
      this.videoUrl.includes('mediadelivery.net/embed') ||
      this.videoUrl.includes('iframe.mediadelivery.net') ||
      this.videoUrl.includes('.b-cdn.net') ||
      this.videoUrl.startsWith('https://iframe.mediadelivery.net/');

    // Convert Bunny embed URLs to HLS playlist URLs for adaptive streaming.
    // If the URL already carries a CDN host (vz-*.b-cdn.net), keep that host so
    // per-org library videos resolve to their own CDN. Only legacy iframe-embed
    // URLs (no CDN host) fall back to the configured shared library host.
    if (isBunnyEmbedUrl) {
      // Extract video ID from the embed URL
      const videoIdMatch = this.videoUrl.match(/([a-f0-9\-]{36})/i);
      if (videoIdMatch && videoIdMatch[1]) {
        const extractedVideoId = videoIdMatch[1];

        // Prefer a CDN host carried in the URL itself, then the video's per-org
        // cdn_hostname (@Input), and only fall back to the shared host for
        // legacy / NULL-library videos. Per-org embed URLs carry no CDN host,
        // so extractCdnHost() is null and cdnHostname is what keeps them on
        // their own host instead of 404'ing on the shared host.
        const cdnHost =
          this.extractCdnHost(this.videoUrl) ||
          this.cdnHostname ||
          environment.bunnySharedCdnHostname;
        const hlsPlaylistUrl = `https://${cdnHost}/${extractedVideoId}/playlist.m3u8`;

        this.processedVideoUrl = hlsPlaylistUrl;
        this.isHlsStream = true;
        return;
      }
    }

    // Check if the URL is already an HLS stream
    if (this.videoUrl.endsWith('.m3u8')) {
      this.isHlsStream = true;
    }

    // Use the URL as-is
    this.processedVideoUrl = this.videoUrl;
  }

  private convertVideoIdToUrl() {
    if (!this.videoId) return;

    // Bare Bunny video ID with no backend-provided playUrls. New uploads route
    // through per-org libraries and the backend usually returns resolved
    // playUrls (Priority 1/2 above), so this path is reached for bare-ID inputs
    // (module/course list rows that only carry bunnyVideoId). Prefer the video's
    // per-org cdn_hostname (@Input); fall back to the shared host only for
    // legacy / NULL-library videos where cdnHostname is empty.
    const cdnHost = this.cdnHostname || environment.bunnySharedCdnHostname;
    const uuidRegex = /^[a-f0-9\-]{36}$/i;
    if (uuidRegex.test(this.videoId)) {
      const hlsPlaylistUrl = `https://${cdnHost}/${this.videoId}/playlist.m3u8`;
      this.processedVideoUrl = hlsPlaylistUrl;
      this.isHlsStream = true;
      return;
    }

    // Check for other Bunny Stream indicators
    const videoIdStr = String(this.videoId);
    if (videoIdStr.includes('bunny') || videoIdStr.includes('mediadelivery')) {
      // Extract video ID if it's embedded in the string
      const videoIdMatch = videoIdStr.match(/([a-f0-9\-]{36})/i);
      if (videoIdMatch && videoIdMatch[1]) {
        const extractedId = videoIdMatch[1];
        const hlsPlaylistUrl = `https://${cdnHost}/${extractedId}/playlist.m3u8`;
        this.processedVideoUrl = hlsPlaylistUrl;
        this.isHlsStream = true;
        return;
      }
    }

  }

  /**
   * Extract a Bunny CDN host (vz-*.b-cdn.net) from a URL, if present.
   * Returns null for iframe-embed URLs that carry no CDN host.
   */
  private extractCdnHost(url: string): string | null {
    const match = url.match(/https?:\/\/([a-z0-9-]+\.b-cdn\.net)/i);
    return match ? match[1] : null;
  }

  // CB-016: Keyboard shortcuts must stay on component (@HostListener limitation)

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent) {
    // Only handle shortcuts when video player is focused or visible
    if (!this.videoPlayer?.nativeElement || this.isThumbnailView) return;

    const target = event.target as HTMLElement;
    // Don't handle shortcuts if user is typing in an input field
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    switch (event.code) {
      case 'Minus':
      case 'NumpadSubtract':
        event.preventDefault();
        this.speedService.decreaseSpeed();
        break;
      case 'Equal':
      case 'NumpadAdd':
        event.preventDefault();
        this.speedService.increaseSpeed();
        break;
      case 'Digit0':
      case 'Numpad0':
        event.preventDefault();
        this.speedService.setPlaybackSpeed(1);
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    // Close speed menu when clicking outside
    if (this.speedService.showSpeedMenu && !target.closest('.speed-menu')) {
      this.speedService.showSpeedMenu = false;
    }
  }
}
